import { useState } from 'react';
import PictureImage from '../Common/PictureImage';
import { Link } from 'react-router-dom';
import { HOMEPAGE_FAQS } from '../../data/homepageFaqs';
import './FAQ.css';

interface FAQItem {
  id?: number;
  question: string;
  answer: string;
}

/** Same image as `FAQSection` on /amazon-fba-prep-services — FAQs left, image right. */
export const DEFAULT_FAQ_SIDE_IMAGE = '/Images/merchent/FAQ_image.webp';

export const HOME_HERO_FAQ_IMAGE =
  '/Images/smart-ecommerce-fulfillment-warehouse-uk-amazon-shopify-tiktok.webp';

type FAQsProps = {
  faqs?: FAQItem[];
  /** Amazon-style split: FAQs left, image right (default on). */
  showSideImage?: boolean;
  sideImageSrc?: string;
  sideImageAlt?: string;
  className?: string;
  title?: string;
  description?: string;
};

const FAQs = ({
  faqs,
  showSideImage = true,
  sideImageSrc = DEFAULT_FAQ_SIDE_IMAGE,
  sideImageAlt = 'UK fulfilment and warehouse operations — Frequently Asked Questions',
  className = '',
  title = 'Pick and Pack Fulfilment FAQs',
  description = 'Answers to common UK pick and pack questions — what it is, how it works, pricing, FBA vs pick and pack, and same-day dispatch from our Milton Keynes fulfilment centre.',
}: FAQsProps = {}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const usedFaqs = faqs?.length ? faqs : HOMEPAGE_FAQS;
  const withSideImage = showSideImage && Boolean(sideImageSrc);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      className={`faqs-section${withSideImage ? ' faqs-section--split' : ''}${className ? ` ${className}` : ''}`}
      id="pick-pack-faqs"
      aria-labelledby="homepage-faqs-heading"
    >
      <div className="faqs-container">
        <div className="faqs-header">
          <div className="faqs-eyebrow">
            <span className="faqs-dash"></span> FAQS
          </div>
          <h2 id="homepage-faqs-heading" className="faqs-title">
            {title.includes("FAQs") ? (
              <>
                {title.replace("FAQs", "")} <span className="faq-blue-text">FAQs</span>
              </>
            ) : title}
          </h2>
          <p className="faqs-description">
            {description}
          </p>
        </div>

        <div
          className={`faqs-content${withSideImage ? ' faqs-content--with-image' : ' faqs-content--no-image'}`}
        >
          <div className="faqs-main">
            <div className="faqs-list">
              {usedFaqs.map((faq, index) => (
                <div key={`faq-${index}`} className={`faq-item ${openIndex === index ? 'faq-open' : ''}`}>
                  <div className="faq-question-wrapper" onClick={() => toggleFAQ(index)}>
                    <h3 className="faq-question">{faq.question}</h3>
                    <button
                      type="button"
                      className={`faq-toggle ${openIndex === index ? 'faq-toggle-open' : ''}`}
                      aria-expanded={openIndex === index}
                      aria-label={openIndex === index ? 'Close answer' : 'Open answer'}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFAQ(index);
                      }}
                    >
                      <svg width="32" height="32" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        {openIndex === index ? (
                          <path d="M5 10H15" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                        ) : (
                          <>
                            <path d="M10 5V15" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                            <path d="M5 10H15" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                  <div className={`faq-answer ${openIndex === index ? 'faq-answer-open' : ''}`}>
                    <p><strong>Answer: </strong>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="faqs-see-more-wrapper">
              <Link to="/fulfillment-frequently-asked-questions" className="faqs-see-more-btn">
                View all FAQs
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M3 8H13M9 4L13 8L9 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>
          </div>

          {withSideImage ? (
            <div className="faqs-image-wrapper">
              <div className="fi-shape-orange"></div>
              <div className="fi-shape-blue"></div>
              <div className="fi-image-inner">
                <PictureImage
                  src={sideImageSrc}
                  alt={sideImageAlt}
                  className="faqs-image"
                  width={1200}
                  height={900}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default FAQs;



